import { useMemo, useState } from 'react'
import { Megaphone } from 'lucide-react'
import { Modal, useToast } from '../admin/primitives.js'
import { Field } from '../admin/form.js'
import { SegmentedControl } from '../admin/pickers.js'
import {
  nomDe, phraseBilan, SALLES, type Tablette,
} from './tablettes-modele.js'
import { geste } from './tablettes-api.js'

/**
 * ANNONCE PLEIN ÉCRAN — à une tablette, à une salle ou à tout le parc.
 *
 * L'aperçu montre ce que verra la tablette. Une salle part en UN appel de
 * groupe (`{ salle }`) ; « Toutes » part avec la liste des identifiants. Les
 * tablettes hors ligne la reçoivent à leur retour si kiosk-api la garde.
 */

type Cible = { type: 'tablette'; tablette: Tablette } | { type: 'parc'; tablettes: Tablette[] }

const DUREES = [{ value: '30', label: '30 s' }, { value: '120', label: '2 min' }, { value: '600', label: '10 min' }, { value: '', label: 'Jusqu’à fermeture' }] as const
const SONS = [{ value: 'carillon', label: 'Carillon' }, { value: 'aucun', label: 'Aucun' }, { value: 'alarme', label: 'Alarme' }] as const

export function AnnonceModale({ cible, onFermer }: { cible: Cible; onFermer: () => void }) {
  const toast = useToast()
  const [texte, setTexte] = useState('')
  const [duree, setDuree] = useState<string>('120')
  const [son, setSon] = useState<string>('carillon')
  const [fermable, setFermable] = useState(true)
  const [envoi, setEnvoi] = useState(false)
  const [cle] = useState(() => crypto.randomUUID())
  const sallesPresentes = useMemo(() => (cible.type === 'parc' ? SALLES.filter((s) => cible.tablettes.some((t) => t.salle === s)) : []), [cible])
  const [visee, setVisee] = useState<string>(sallesPresentes[0] ?? 'Toutes')

  const nb = cible.type === 'tablette' ? 1
    : visee === 'Toutes' ? cible.tablettes.length : cible.tablettes.filter((t) => t.salle === visee).length
  const propre = texte.trim()
  const manque = !propre ? 'Écrivez le texte de l’annonce.' : propre.length > 280 ? '280 caractères au plus.' : nb === 0 ? 'Aucune tablette visée.' : null

  const envoyer = async (effacer = false) => {
    if ((!effacer && manque) || envoi) return
    setEnvoi(true)
    // Contrat : un texte vide EFFACE l'annonce affichée.
    const annonce = effacer ? { texte: '' } : { texte: propre, son, duree: duree ? Number(duree) : null, fermable }
    const r = cible.type === 'tablette'
      ? await geste('POST', `/api/v1/tablettes/${encodeURIComponent(cible.tablette.id)}/annonce`, annonce, cle)
      : await geste('POST', '/api/v1/tablettes/groupe/annonce', {
        cible: visee === 'Toutes' ? { ids: cible.tablettes.map((t) => t.id) } : { salle: visee }, annonce,
      }, cle)
    setEnvoi(false)
    if (r.ok) {
      const quoi = effacer ? 'Annonce effacée' : 'Annonce envoyée'
      toast('ok', cible.type === 'tablette' ? `${quoi}.` : phraseBilan(r.data?.['bilan'] as never, quoi))
      onFermer()
    }
    else toast('error', r.message)
  }

  return (
    <Modal open onClose={onFermer} ariaLabel="Annonce plein écran" width={920} owning>
      <div className="lc-modal__head">
        <div>
          <h2 className="lc-modal__title">Annonce plein écran</h2>
          <p className="lc-hint">{cible.type === 'tablette' ? nomDe(cible.tablette) : 'Chaque envoi est inscrit au journal, avec votre nom.'}</p>
        </div>
      </div>
      <div className="lc-modal__body tb-annonce">
        <div className="tb-annonce__form">
          {cible.type === 'parc' ? (
            <Field label="Tablettes visées" helper={`${nb} tablette${nb > 1 ? 's' : ''} · les tablettes hors ligne la recevront à leur retour`}>
              <div className="tb-chips" role="group" aria-label="Tablettes visées">
                {[...sallesPresentes, 'Toutes'].map((s) => (
                  <button key={s} type="button" className={`tb-chip${visee === s ? ' tb-chip--on' : ''}`} aria-pressed={visee === s} onClick={() => setVisee(s)}>{s}</button>
                ))}
              </div>
            </Field>
          ) : null}
          <Field label="Texte" helper={`${propre.length} / 280`}>
            <textarea className="lc-input tb-textarea" rows={3} maxLength={280} value={texte} onChange={(e) => setTexte(e.target.value)}
              placeholder="Fermeture des bars dans 15 minutes" aria-label="Texte de l’annonce" />
          </Field>
          <Field label="Durée">
            <SegmentedControl value={duree} options={DUREES.map((d) => ({ value: d.value, label: d.label }))} onChange={setDuree} ariaLabel="Durée" size="sm" />
          </Field>
          <Field label="Son">
            <SegmentedControl value={son} options={SONS.map((s) => ({ value: s.value, label: s.label }))} onChange={setSon} ariaLabel="Son" size="sm" />
          </Field>
          <label className="tb-case"><input type="checkbox" checked={fermable} onChange={(e) => setFermable(e.target.checked)} /> Se ferme d’un toucher</label>
        </div>
        <div className="tb-annonce__apercu" aria-label="Aperçu sur la tablette">
          <span className="lc-hint">Aperçu sur la tablette</span>
          <div className="tb-apercu">
            <Megaphone size={36} aria-hidden="true" />
            <p className="tb-apercu__texte">{propre || 'Votre annonce'}</p>
            {fermable ? <span className="tb-apercu__aide">Toucher pour fermer</span> : null}
          </div>
        </div>
      </div>
      <footer className="lc-modal__foot tb-modal-foot">
        {manque && texte ? <span className="lc-hint" role="status">{manque}</span> : <span />}
        <div className="tb-actions">
          <button type="button" className="lc-btn" onClick={onFermer}>Annuler</button>
          <button type="button" className="lc-btn" disabled={envoi || nb === 0} onClick={() => { void envoyer(true) }}>Effacer l’annonce affichée</button>
          <button type="button" className="lc-btn lc-btn--primary" disabled={!!manque || envoi} onClick={() => { void envoyer() }}>
            {envoi ? 'Envoi…' : `Envoyer à ${nb} tablette${nb > 1 ? 's' : ''}`}
          </button>
        </div>
      </footer>
    </Modal>
  )
}
